import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../styles/Header.css';

const navLinks = [
  { label: "Home", href: "/" },
  { label: "About", href: "/about" },
  { label: "Programs", href: "/programs" },
  { label: "Contact", href: "/contact" },
  { label: "Blogs", href: "/blogs" },
  { label: "Legal & Compliance", href: "/legal" },
];

export function Header({ onLogin, onSignup, user, onLogout }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeLink, setActiveLink] = useState("/");
  const location = useLocation();

  useEffect(() => {
    // Set active link based on current route
    const currentPath = location.pathname;
    if (currentPath.startsWith('/blog')) {
      setActiveLink('/blogs');
    } else {
      setActiveLink(currentPath);
    }
  }, [location]);

  useEffect(() => {
    const handleScroll = () => {
      // Only handle scroll-based active link on homepage
      if (location.pathname !== '/') return;
      
      const sections = ['home', 'about', 'programs', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveLink(`#${sections[i]}`);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location]);

  const handleNavClick = (href) => {
    setIsMenuOpen(false);
    
    // If it's a hash link on the homepage, scroll to section
    if (href.startsWith('#') && location.pathname === '/') {
      const element = document.getElementById(href.substring(1));
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const isExternalLink = (href) => href.startsWith('/');

  return (
    <header className="header">
      <div className="header-container">
        <nav className="nav">
          <Link to="/" className="logo" onClick={() => setIsMenuOpen(false)}>
            <img src="/assets/logo-img.jpg" alt="" className="logo-img" />
            <span className="logo-text">Get Fit with <span className="logo-highlight">Daniella</span></span>
          </Link>

          <ul className={`nav-links ${isMenuOpen ? 'nav-open' : ''}`}>
            {navLinks.map((link) => (
              <li key={link.label}>
                {isExternalLink(link.href) ? (
                  <Link
                    to={link.href}
                    className={`nav-link ${activeLink === link.href ? 'active' : ''}`}
                    onClick={() => handleNavClick(link.href)}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <Link
                    to={link.href.startsWith('#') ? `/${link.href}` : link.href}
                    className={`nav-link ${activeLink === link.href ? 'active' : ''}`}
                    onClick={() => handleNavClick(link.href)}
                    data-ga-event={link.label}
                  >
                    {link.label}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          <div className="header-cta">
            {user ? (
              <>
                <span className="user-name">{user.user_metadata?.full_name || user.email}</span>
                <button className="btn btn-secondary" onClick={onLogout} data-ga-event="Logout">Logout</button>
              </>
            ) : (
              <>
                <button className="btn btn-secondary" onClick={onLogin} data-ga-event="Login">Login</button>
                <button className="btn btn-primary" onClick={onSignup} data-ga-event="Sign Up">Sign Up</button>
              </>
            )}
          </div>

          <button
            className="menu-toggle"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </svg>
            )}
          </button>
        </nav>
      </div>

      {isMenuOpen && (
        <div className="mobile-menu">
          <ul className="mobile-nav-links">
            {navLinks.map((link) => (
              <li key={link.label}>
                {isExternalLink(link.href) ? (
                  <Link
                    to={link.href}
                    className={`mobile-nav-link ${activeLink === link.href ? 'active' : ''}`}
                    onClick={() => setIsMenuOpen(false)}
                    data-ga-event={link.label}
                  >
                    {link.label}
                  </Link>
                ) : (
                  <Link
                    to={link.href.startsWith('#') ? `/${link.href}` : link.href}
                    className={`mobile-nav-link ${activeLink === link.href ? 'active' : ''}`}
                    onClick={() => setIsMenuOpen(false)}
                    data-ga-event={link.label}
                  >
                    {link.label}
                  </Link>
                )}
              </li>
            ))}
            {user ? (
              <>
                <li className="mobile-cta">
                  <span className="mobile-user-name">{user.user_metadata?.full_name || user.email}</span>
                </li>
                <li className="mobile-cta">
                  <button className="btn btn-secondary btn-full" onClick={() => { onLogout(); setIsMenuOpen(false); }} data-ga-event="Logout">Logout</button>
                </li>
              </>
            ) : (
              <>
                <li className="mobile-cta">
                  <button className="btn btn-secondary btn-full" onClick={() => { onLogin(); setIsMenuOpen(false); }} data-ga-event="Login">Login</button>
                </li>
                <li className="mobile-cta">
                  <button className="btn btn-primary btn-full" onClick={() => { onSignup(); setIsMenuOpen(false); }} data-ga-event="Sign Up">Sign Up</button>
                </li>
              </>
            )}
          </ul>
        </div>
      )}
    </header>
  );
}
